import { createActionHandler, createReducer, OperationStatus } from '@pressreader/appstore';
import { userChangeDone } from '@pressreader/authentication-core';
import { IIssueCalendarInfo, IIssueInfo, IIssueInfoPageKey } from '@pressreader/content-types';
import { purchaseDone } from '@pressreader/purchase';

import { buildCidDateLanguageKey, isLanguageKeyOfCidDate } from '../../utils';
import {
    loadIssueCalendarInfoBegan,
    loadIssueCalendarInfoDone,
    loadIssueCalendarInfoFailed,
    loadIssueInfoBegan,
    loadIssueInfoDone,
    loadIssueInfoFailed,
    loadIssuePageKeysDone,
} from '../actions/issueinfo';

interface State {
    info: {
        [cidDateLanguageKey: string]: {
            info: IIssueInfo | null;
            loading: OperationStatus;
            generation: number;
        };
    };

    calendar: {
        [cid: string]: {
            calendar: IIssueCalendarInfo | null;
            loading: OperationStatus;
            generation: number;
        };
    };

    /**
     * Bumped on `purchaseDone`. A cache entry whose `generation` is older than this
     * is treated as a miss by the loaders and re-fetched (stale-while-revalidate).
     */
    infoGeneration: number;
}

const initialState: State = {
    info: {},
    calendar: {},
    infoGeneration: 0,
};

function getIssueInfoState(state: State, cidDateLanguageKey: string) {
    if (cidDateLanguageKey in state.info) {
        return state.info[cidDateLanguageKey];
    }

    return { info: null, loading: OperationStatus.None, generation: state.infoGeneration };
}

function getIssueCalendarInfoState(state: State, cid: string) {
    if (cid in state.calendar) {
        return state.calendar[cid];
    }

    return { calendar: null, loading: OperationStatus.None, generation: state.infoGeneration };
}

/**
 * Every key the issue's info is cached under for one cidDate: the language-free key and the
 * language keys. Page keys unlock the issue's pages, which no translation changes, so page keys
 * are carried across all of them.
 */
function getIssueInfoKeysOfCidDate(info: State['info'], cidDateKey: string): string[] {
    return Object.keys(info).filter(key => key === cidDateKey || isLanguageKeyOfCidDate(key, cidDateKey));
}

/**
 * A full issue-info re-fetch (e.g. after a purchase) re-derives every page with
 * `pageKey: null` — `mapIssueInfoPages` never maps keys. Carry over the keys any cached
 * entry of the issue already holds so a refresh doesn't briefly revert pages to low-res
 * before the keys reload.
 */
function carryForwardPageKeys(next: IIssueInfo, cachedInfos: IIssueInfo[]): IIssueInfo {
    if (!cachedInfos.length) {
        return next;
    }

    const prevKeys = new Map<number, IIssueInfoPageKey>();
    cachedInfos.forEach(cachedInfo => {
        cachedInfo.pages.forEach(page => {
            if (page.pageKey && !prevKeys.has(page.pageNumber)) {
                prevKeys.set(page.pageNumber, page.pageKey);
            }
        });
    });

    return {
        ...next,
        pages: next.pages.map(page => {
            const prevKey = prevKeys.get(page.pageNumber);
            // Only onto pages the fresh fetch still reports as readable. On an entitlement *loss*
            // (a HotSpot radiant window expiring, sign-out) the re-fetch correctly returns
            // `isLocked: true`, and resurrecting the dead key here makes `loadIssuePageKeysDone`
            // force `isLocked` back to false — leaving the issue readable with no lock UI. The gain
            // direction this carry-forward exists for is unaffected: those pages come back unlocked.
            return prevKey && !page.isLocked ? { ...page, pageKey: prevKey } : page;
        }),
    };
}

const reducer = createReducer(
    initialState,
    createActionHandler(userChangeDone, state => ({
        ...state,
        info: {},
        calendar: {},
    })),
    createActionHandler(purchaseDone, state => ({
        ...state,
        // Soft cache invalidation: a purchase can change entitlement for any cached
        // issue. Bump the generation rather than wiping `info`/`calendar` — entries
        // keep rendering (no rug-pull for the open issue) and the loaders re-fetch a
        // stale-generation entry on next access.
        infoGeneration: state.infoGeneration + 1,
    })),
    createActionHandler(loadIssueInfoBegan, (state, { payload }) => {
        const { cid, issueDate, languageCode } = payload;

        const key = buildCidDateLanguageKey({ cid, issueDate, languageCode });

        const issueInfoState = {
            ...getIssueInfoState(state, key),
            loading: OperationStatus.Pending,
        };

        return {
            ...state,
            info: {
                ...state.info,
                [key]: issueInfoState,
            },
        };
    }),
    createActionHandler(loadIssueInfoDone, (state, { payload }) => {
        const { latest, issueInfo, languageCode } = payload;

        // A response in the newspaper's own language IS the original: it answers a request that
        // named that language and one that named none alike, so it is cached under both keys. The
        // loaders alias between the two in either direction, and a request of either shape that
        // arrives later is served from the cache instead of fetching the whole issue again.
        const isOriginalUnderLanguageKey = !!languageCode && languageCode === issueInfo.newspaper.isoLanguage;
        const languageCodes: (string | undefined)[] = isOriginalUnderLanguageKey ? [languageCode, undefined] : [languageCode];

        const info = { ...state.info };
        if (latest) {
            delete info[buildCidDateLanguageKey({ cid: issueInfo.newspaper.cid, languageCode })];
        }

        const keys = languageCodes.map(code => buildCidDateLanguageKey(issueInfo.issueCidDateKey, code));
        // Page keys cached under any entry of the issue survive the re-fetch - see `carryForwardPageKeys`.
        const cachedInfos = getIssueInfoKeysOfCidDate(info, issueInfo.issueCidDateKey)
            .map(key => info[key].info)
            .filter((cachedInfo): cachedInfo is IIssueInfo => cachedInfo !== null);
        const entry = {
            info: carryForwardPageKeys(issueInfo, cachedInfos),
            loading: OperationStatus.Done,
            generation: state.infoGeneration,
        };
        keys.forEach(key => {
            info[key] = entry;
        });

        return {
            ...state,
            info,
        };
    }),
    createActionHandler(loadIssueInfoFailed, (state, { payload }) => {
        const { cid, issueDate, languageCode } = payload;

        const key = buildCidDateLanguageKey({ cid, issueDate, languageCode });

        const issueInfoState = {
            ...getIssueInfoState(state, key),
            loading: OperationStatus.Failed,
        };

        return {
            ...state,
            info: {
                ...state.info,
                [key]: issueInfoState,
            },
        };
    }),
    createActionHandler(loadIssuePageKeysDone, (state, { payload }) => {
        const { cidDateKey, pageKeys } = payload;

        const withPageKeys = (issueInfo: IIssueInfo): IIssueInfo => ({
            ...issueInfo,
            pages: issueInfo.pages.map(page => {
                const pageKey = pageKeys[page.pageNumber] || null;

                return {
                    ...page,
                    pageKey,
                    isLocked: pageKey ? false : page.isLocked,
                };
            }),
        });

        // Every entry of the issue holding cached info takes the keys, whatever its loading status:
        // an entry left behind — one under a refresh in flight — keeps the obsolete keys its cached
        // info was fetched with, and `carryForwardPageKeys` would then hand them back to the whole
        // issue when that refresh lands. Entries aliasing one info object (the newspaper-language
        // response cached under both keys) keep sharing the keyed one.
        const keyedInfos = new Map<IIssueInfo, IIssueInfo>();
        const info = { ...state.info };
        getIssueInfoKeysOfCidDate(info, cidDateKey).forEach(key => {
            const issueInfoState = info[key];
            if (!issueInfoState.info) {
                return;
            }

            const keyedInfo = keyedInfos.get(issueInfoState.info) ?? withPageKeys(issueInfoState.info);
            keyedInfos.set(issueInfoState.info, keyedInfo);
            info[key] = { ...issueInfoState, info: keyedInfo };
        });

        return {
            ...state,
            info,
        };
    }),
    createActionHandler(loadIssueCalendarInfoBegan, (state, { payload }) => {
        const { cid } = payload;

        const issueCalendarInfo = {
            ...getIssueCalendarInfoState(state, cid),
            loading: OperationStatus.Pending,
        };

        return {
            ...state,
            calendar: {
                ...state.calendar,
                [cid]: issueCalendarInfo,
            },
        };
    }),
    createActionHandler(loadIssueCalendarInfoDone, (state, { payload }) => {
        const { cid, calendar } = payload;

        return {
            ...state,
            calendar: {
                ...state.calendar,
                [cid]: { calendar, loading: OperationStatus.Done, generation: state.infoGeneration },
            },
        };
    }),
    createActionHandler(loadIssueCalendarInfoFailed, (state, { payload }) => {
        const { cid } = payload;

        const issueCalendarInfo = {
            ...getIssueCalendarInfoState(state, cid),
            loading: OperationStatus.Failed,
        };

        return {
            ...state,
            calendar: {
                ...state.calendar,
                [cid]: issueCalendarInfo,
            },
        };
    }),
);

export { reducer, State };
